import React, { useContext, useEffect, useState } from "react";
import axiosInstance from "../axiosConfig";
import { AuthContext } from "../AuthContext";
import Logo from "../assets/Img/Logo-1.png";
import User from "../assets/Img/User.gif";
import DataTable from "react-data-table-component";

function UserTickets() {
  const { user, logout } = useContext(AuthContext);
  const [department, setDepartment] = useState("Not Assigned");
  const [ticketData, setTicketData] = useState([]);
  const [hover, setHover] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showModal, setShowModal] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    UserID: "",
    SupervisorID: "",
    Department: "",
    Subject: "",
    TicketDescription: ""
  });
  const [modalMessage, setModalMessage] = useState("");
  const [modalLoading, setModalLoading] = useState(false);

  // Store the list of supervisors for the user's department
  const [supervisors, setSupervisors] = useState([]);
  
  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;


  // Update isMobile on window resize.
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch all users to get the logged-in user's department.
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get("/api/AllUsers");
        const users = response.data;
        const userDepartment = users.find(
          (u) => u.EmployeeID === user?.EmployeeID
        )?.Department;
        setDepartment(userDepartment || "Not Assigned");
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    if (user?.EmployeeID) {
      fetchUsers();
    }
  }, [user?.EmployeeID]);

  // Fetch tickets raised by the logged-in user.
  useEffect(() => {
    const fetchTickets = async () => {
      try {
        // Call GET API with the user's EmployeeID.
        const response = await axiosInstance.get(`/api/User-tickets/${user?.EmployeeID}`);
        setTicketData(response.data);
      } catch (error) {
        console.error("Error fetching tickets:", error);
      }
    };
    if (user?.EmployeeID) {
      fetchTickets();
    }
  }, [user?.EmployeeID]);

  // 3. Once we know the user's department, fetch the supervisors for that department.
  useEffect(() => {
    const fetchSupervisors = async () => {
      if (!department || department === "Not Assigned") return;

      try {
        const response = await axiosInstance.get(`/api/supervisor/${department}`);
        setSupervisors(response.data);
      } catch (error) {
        console.error("Error fetching supervisors:", error);
      }
    };

    fetchSupervisors();
  }, [department]);

  // Auto check session expiration.
  useEffect(() => {
    const checkSessionExpiration = () => {
      const sessionExpiration = localStorage.getItem("sessionExpiration");
      const currentTime = new Date();
      if (!sessionExpiration || currentTime > new Date(sessionExpiration)) {
        window.location.reload();
      }
    };
    checkSessionExpiration();
    const interval = setInterval(checkSessionExpiration, 5000);
    return () => clearInterval(interval);
  }, []);


  const showAlert = (message, type) => {
    const alertPlaceholder = document.getElementById("alertPlaceholder");
    const alertHTML = `
      <div class="alert alert-${type} alert-dismissible fade show" role="alert">
        <strong>${type === "success" ? "Success!" : "Error!"}</strong> ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>`;
    alertPlaceholder.innerHTML = alertHTML;
    setTimeout(() => {
      alertPlaceholder.innerHTML = "";
    }, 3000);
  };

  // Function to handle ticket confirmation via PATCH.
  const handleConfirm = async (ticketId) => {
    try {
      const response = await axiosInstance.patch(`/api/User-tickets/${ticketId}/confirm`);
      showAlert(response.data.message, "success");
      // Re-fetch tickets after confirming.
      const ticketsRes = await axiosInstance.get(`/api/User-tickets/${user?.EmployeeID}`);
      setTicketData(ticketsRes.data);
    } catch (error) {
      console.error("Error confirming ticket:", error);
      showAlert("Failed to confirm ticket.", "danger");
    }
  };



    // Open the modal and pre-populate the ticket form with user's ID & department
    const openModal = () => {
      setShowModal(true);
      setTicketForm({
        UserID: user?.EmployeeID || "",
        SupervisorID: "",
        Department: department,
        Subject: "",
        TicketDescription: ""
      });
    };
      
    const closeModal = () => {
      setShowModal(false);
      setModalMessage("");
      setTicketForm({
        UserID: user?.EmployeeID || "",
        SupervisorID: "",
        Department: department,
        Subject: "",
        TicketDescription: ""
      });
    };
  
    const handleModalInputChange = (e) => {
      setTicketForm({ ...ticketForm, [e.target.name]: e.target.value });
    };
  
    const handleModalSubmit = async (e) => {
      e.preventDefault();
      setModalLoading(true);
      try {
        const response = await axiosInstance.post("/api/User-tickets", ticketForm);
        setModalMessage(response.data.message);
        closeModal();
        showAlert(response.data.message, "success");
        // Re-fetch tickets after successful creation.
        const ticketsRes = await axiosInstance.get(`/api/User-tickets/${user?.EmployeeID}`);
        setTicketData(ticketsRes.data);
      } catch (error) {
        console.error("Error creating ticket:", error);
        setModalMessage("Failed to create ticket.");
        showAlert("Failed to create ticket.", "danger");
      } finally {
        setModalLoading(false);
      }
    };
    // derive pagination values
    const totalPages = Math.ceil(ticketData.length / itemsPerPage);
    const paginatedData = ticketData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );


  // Define columns for the DataTable.
  const columns = [
    {
      name: "Ticket ID",
      selector: (row) => row.TicketID,
      sortable: true,
      width: "100px"
    },
    {
      name: "Department",
      selector: (row) => row.Department,
      sortable: true
    },
    {
      name: "Subject",
      selector: (row) => row.Subject,
      sortable: true,
      wrap: true
    },
    {
      name: "Description",
      selector: (row) => row.TicketDescription,
      sortable: false,
      wrap: true,
      grow: 2
    },
    {
      name: "Status",
      selector: (row) => row.TicketStatus,
      sortable: true,
      width: "120px"
    },
    {
      name: "Raised Date",
      selector: (row) => {
        const date = new Date(row.CreatedDate);
        return date.toLocaleDateString("en-GB");
      },
      sortable: true,
      width: "150px"
    },
    {
      name: "Solved by",
      selector: (row) => row.SolvedBy || "N/A",
      sortable: true,
      width: "120px"
    },
    {
        name: "Action",
        cell: (row) =>
          row.TicketStatus.toLowerCase() === "solved" && row.isConfirmed !== "Yes" ? (
            <button className="btn btn-sm btn-success" onClick={() => handleConfirm(row.TicketID)}>
              Confirm
            </button>
          ) : (
            ""
          ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: "120px"
      }
  ];

//   // Open the modal and pre-populate the UserID and Department.
//   const openModal = () => {
//     setShowModal(true);
//     setTicketForm({
//       ...ticketForm,
//       UserID: user?.EmployeeID || "",
//       Department: department
//     });
//   };


  return (
    <div>
      <div id="alertPlaceholder"></div>
      <nav
        className="navbar navbar-expand-lg bg-body-white"
        style={{ boxShadow: "0 1px 5px rgba(0, 0, 0, 0.2)" }}
      >
        <div className="container-fluid">
          <a className="navbar-brand">
            <img
              src={Logo}
              className="img-fluid rounded-top"
              alt="Logo"
              style={{ height: "40px", marginLeft: "10px" }}
            />
          </a>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarSupportedContent"
            aria-controls="navbarSupportedContent"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarSupportedContent">
            <ul className="navbar-nav me-auto mb-2 mb-lg-0">
              <li className="nav-item" style={{ marginLeft: "10px" }}>
                <a
                  className="nav-link active"
                  aria-current="page"
                  href="/User-dashboard"
                  onMouseEnter={() => setHover(true)}
                  onMouseLeave={() => setHover(false)}
                  style={{ color: hover ? "#ff0000" : "inherit" }}
                >
                  Home
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="collapse navbar-collapse" id="navbarSupportedContent">
          <ul className="navbar-nav me-auto mb-2 mb-lg-0"></ul>
          {/* <span style={{ marginLeft: isMobile ? "0" : "-21rem" }}>
            Welcome, {user?.Name}!{" "}
          </span> */}
          <div className="dropdown">
            <a
              className="d-flex align-items-center text-decoration-none dropdown-toggle ml-5"
              role="button"
              id="dropdownMenuLink"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              style={{
                marginRight: "20px",
                justifyContent: "end",
                marginLeft: "10px",
                ...(isMobile && { marginTop: "-20px" })
              }}
            >
              <img
                src={User}
                alt="Profile"
                className="rounded-circle"
                style={{ width: "40px", height: "40px", objectFit: "cover" }}
              />
            </a>
            <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownMenuLink">
              <li>
                <a className="dropdown-item text-danger" onClick={logout}>
                  Logout
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>
      <div className="dashboard-container" style={{ background: "#f7f9fc", minHeight: "100vh", padding: "10px" }}>
        <main style={{ padding: "10px" }}>
          <div
            className="container mt-3"
            style={{
              boxShadow: "0 1px 5px rgba(0, 0, 0, 0.51)",
              borderRadius: "5px",
              padding: "6px"
            }}
          >
            <button onClick={openModal} className="btn btn-primary mb-3">
              Create Ticket
            </button>
            {/* Show table for non-mobile devices, card layout for mobile */}
            {!isMobile ? (
              <DataTable
                columns={columns}
                data={ticketData}
                pagination
                highlightOnHover
              />
            ) : (
               <div className="table-mobile">
                {paginatedData.map((ticket, index) => (
                  <div key={index} className="card mb-2">
                    <div className="card-body">
                      <h5 className="card-title">Ticket #{ticket.TicketID}</h5>
                      <p className="card-text"><strong>Subject:</strong> {ticket.Subject}</p>
                      <p className="card-text"><strong>Description:</strong> {ticket.TicketDescription}</p>
                      <p className="card-text"><strong>Status:</strong> {ticket.TicketStatus}</p>
                      <p className="card-text"><strong>Solved By:</strong> {ticket.SolvedBy}</p>
                      <p className="card-text">
                        <strong>Date:</strong> {new Date(ticket.CreatedDate).toLocaleDateString("en-GB")}
                      </p>
                      {ticket.TicketStatus.toLowerCase() === "solved" && ticket.isConfirmed !== "Yes" && (
                        <button className="btn btn-sm btn-success" onClick={() => handleConfirm(ticket.TicketID)}>
                          Confirm
                        </button>
                      )}
                      {ticket.isConfirmed === "Yes" && ticket.Confirmed_Time && (
                        <p className="card-text">
                          <strong>Confirmed On:</strong> {new Date(ticket.Confirmed_Time).toLocaleDateString("en-GB")}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                {/* pagination controls */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between mt-4">
                    <button
                      className="btn btn-primary mb-3"
                      onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button
                      className="btn btn-primary mb-3"
                      onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Create Ticket Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <span className="close" onClick={closeModal}>
              &times;
            </span>
            <h4>Create New Ticket</h4>
            <form onSubmit={handleModalSubmit}>
              <div className="mb-3">
                <label className="form-label">User ID</label>
                <input
                  type="text"
                  name="UserID"
                  value={ticketForm.UserID}
                  onChange={handleModalInputChange}
                  className="form-control"
                  required
                  readOnly
                />
              </div>
              <div className="mb-3">
                {/* <label className="form-label">Supervisor ID</label>
                <input
                  type="text"
                  name="SupervisorID"
                  value={ticketForm.SupervisorID}
                  onChange={handleModalInputChange}
                  className="form-control"
                  required
                /> */}
                 <label className="form-label">Supervisor</label>
                <select
                  name="SupervisorID"
                  value={ticketForm.SupervisorID}
                  onChange={handleModalInputChange}
                  className="form-control"
                  required
                >
                  <option value="">Select Supervisor</option>
                  {supervisors.map((sup) => (
                    <option key={sup.EmployeeID} value={sup.EmployeeID}>
                      {sup.Supervisor_Name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Department</label>
                <input
                  type="text"
                  name="Department"
                  value={ticketForm.Department}
                  onChange={handleModalInputChange}
                  className="form-control"
                  required
                  readOnly
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Subject</label>
                <input
                  type="text"
                  name="Subject"
                  value={ticketForm.Subject}
                  onChange={handleModalInputChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Ticket Description</label>
                <textarea
                  name="TicketDescription"
                  value={ticketForm.TicketDescription}
                  onChange={handleModalInputChange}
                  className="form-control"
                  required
                />
              </div>
              <div className="mb-3">
                <button type="submit" className="btn btn-success" disabled={modalLoading}>
                  {modalLoading ? "Submitting..." : "Submit Ticket"}
                </button>
              </div>
            </form>
            {modalMessage && <div className="alert alert-info">{modalMessage}</div>}
          </div>
        </div>
      )}

      {/* Inline Responsive CSS */}
      <style jsx="true">{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 10px;
        }
        .modal-content {
          background-color: #fff;
          padding: 20px;
          width: 100%;
          max-width: 500px;
          border-radius: 5px;
          position: relative;
        }
        .modal-content h2 {
          margin-top: 0;
        }
        .close {
          position: absolute;
          right: 15px;
          top: 10px;
          font-size: 28px;
          cursor: pointer;
        }
        @media (max-width: 768px) {
          .modal-content {
            width: 95%;
            padding: 15px;
          }
          nav .navbar-brand img {
            height: 35px;
          }
          nav span {
            font-size: 14px;
          }
          .dashboard-container {
            padding: 5px;
          }
        }
      `}</style>
    </div>
  );
}

export default UserTickets;
